import { useEffect } from 'react';
import useSeo, { type SeoData } from '@/hooks/useSeo';

export default function ArtigoSeguranca() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoData: SeoData = {
    title: 'Segurança no CSSBuy Spreadsheet: Dicas Essenciais para Comprar da China',
    description: 'Dicas essenciais de segurança para usar o CSSBuy Spreadsheet. Principais riscos ao importar da China, como se proteger de taxas alfandegárias e evitar erros comuns nas compras.',
    keywords: 'segurança CSSBuy, comprar China com segurança, riscos importação, CSSBuy Spreadsheet seguro, proteção compras China',
    canonicalPath: '/artigo/seguranca-cssbuy-spreadsheet',
    ogType: 'article',
    ogImage: 'https://readdy.ai/api/search-image?query=Elegant%20padlock%20and%20shield%20icon%20on%20a%20clean%20minimal%20surface%20with%20shopping%20bags%20nearby%2C%20warm%20natural%20lighting%2C%20cream%20and%20beige%20background%2C%20editorial%20concept%20photography%2C%20safety%20and%20security%20theme%2C%20soft%20shadows&width=1200&height=630&seq=artigo-seguranca-hero-seo&orientation=landscape',
    schemaJsonLd: {
      '@type': 'BlogPosting',
      headline: 'Segurança no CSSBuy Spreadsheet: Dicas Essenciais',
      description: 'Guia completo sobre segurança no CSSBuy Spreadsheet com dicas para evitar riscos e proteger suas compras internacionais.',
      datePublished: '2026-06-04',
      dateModified: '2026-06-17',
      author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
    },
  };
  useSeo(seoData);

  return (
    <main className="min-h-screen bg-background-50">
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <nav className="mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Home
          </a>
        </nav>

        <header className="mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide mb-6">
            SEGURANÇA
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Segurança no CSSBuy Spreadsheet: Dicas Essenciais
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
            Proteja suas compras internacionais com nossas dicas de segurança. Evite
            taxas excessivas, atrasos na entrega e erros comuns ao importar da China.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
            <span>Atualizado: Junho 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>Leitura: 7 min</span>
          </div>
        </header>

        <div className="w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-14">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20padlock%20and%20shield%20icon%20on%20a%20clean%20minimal%20surface%20with%20shopping%20bags%20nearby%2C%20warm%20natural%20lighting%2C%20cream%20and%20beige%20background%2C%20editorial%20concept%20photography%2C%20safety%20and%20security%20theme%2C%20soft%20shadows&width=1200&height=630&seq=artigo-seguranca-hero-v2&orientation=landscape"
            alt="Segurança CSSBuy Spreadsheet"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Principais Riscos ao Comprar da China
          </h2>
          <p className="text-foreground-700 leading-relaxed mb-5">
            Importar produtos da China usando o <strong>CSSBuy Spreadsheet</strong> é
            seguro, mas é importante estar ciente dos riscos para tomar as precauções
            necessárias.
          </p>

          <div className="space-y-4">
            {[
              {
                title: 'Taxas Alfandegárias',
                desc: 'A Receita Federal pode taxar produtos importados. Declare valores realistas e esteja preparado para possíveis cobranças adicionais.',
              },
              {
                title: 'Atrasos na Entrega',
                desc: 'O prazo pode variar de 15 a 60 dias. Escolha métodos de frete rastreados para acompanhar seu pedido.',
              },
              {
                title: 'Erros de Tamanho e Qualidade',
                desc: 'Sempre verifique tabelas de medidas e avaliações de outros compradores antes de finalizar o pedido.',
              },
              {
                title: 'Lojas Não Confiáveis',
                desc: 'Use apenas lojas com boa reputação e histórico positivo de vendas no CSSBuy Spreadsheet.',
              },
            ].map((risk, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-xl p-5 border border-background-200/60"
              >
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex-shrink-0 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                    <i className="ri-shield-flash-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">
                      {risk.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {risk.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Como se Proteger
          </h2>
          <ul className="space-y-3">
            {[
              'Declare valores corretos na alfândega para evitar multas',
              'Use sempre frete rastreado para acompanhar seu pedido',
              'Verifique a reputação da loja antes de comprar',
              'Evite excesso de peso em um único pacote',
              'Guarde todos os comprovantes e registros de compra',
              'Utilize o CSSBuy Spreadsheet para manter histórico organizado',
            ].map((tip, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground-700">
                <span className="text-secondary-500 mt-0.5">✓</span>
                {tip}
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
            Compre com Segurança
          </h2>
          <p className="text-background-50/85 mb-6 max-w-md mx-auto leading-relaxed">
            Use o CSSBuy Spreadsheet para organizar suas compras e minimizar riscos
            na importação.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
          >
            CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </section>

        <section className="mt-12 pt-10 border-t border-background-200/70">
          <h3 className="text-lg font-semibold text-foreground-900 mb-4">
            Leia também
          </h3>
          <div className="flex flex-wrap gap-3">
            <a
              href="/artigo/cssbuy-spreadsheet-guia-completo"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Guia Completo CSSBuy
            </a>
            <a
              href="/artigo/como-usar-cssbuy-spreadsheet"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Como Usar CSSBuy
            </a>
            <a
              href="/"
              className="px-4 py-2 bg-primary-100 hover:bg-primary-200 rounded-full text-sm text-primary-700 transition-colors font-medium"
            >
              ← Voltar para Home
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}